// Copyright (c) Docugami, Inc. All rights reserved.

import { ZIndices, primaryTheme } from "components/shell/presentation/AppStyles";
import { IconDescriptor, IconRenderer } from "components/work_path/IconRenderer";
import { dgIconNames } from "hooks/dgIcons";
import { IDocumentDgml, DgmlInstancesSingleton, useDocumentDgml } from "hooks/documentDgml";
import { ChunkInstance } from "networking/api";
import { useEffect, useRef } from "react";
import { useDragLayer } from "react-dnd";
import styled from "styled-components";
import { cardShadow } from "./CardStyles";
import { DndItem, isDndChunk } from "./DndTypes";
import { ChunkStructure } from "store/dgml/dgmlTypes";

const Root = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: ${ZIndices.ChunkDragVisual};
`

export const ChunkDragLayer = () : JSX.Element | null => {
    const visualRef = useRef<HTMLDivElement | null>(null);

    const {isDragging, currentOffset, item} = useDragLayer(m => ({
        isDragging: m.isDragging(),
        currentOffset: m.getClientOffset(),
        item: m.getItem() as DndItem | undefined,
    }));

    const dgml = useDocumentDgml();

    useEffect(() => {
        if (!!visualRef.current && !!currentOffset) {
            visualRef.current.style.transform = `translate(${currentOffset.x}px, ${currentOffset.y}px)`
        }
    }, [currentOffset]);

    return isDragging && dgml.isInitialized() && !!item && isDndChunk(item) ? (
        <Root ref={visualRef}>
            <ChunkDragVisual isVisible={true} chunk={item.instance} dgml={dgml} />
        </Root>
    ) : null;
}

interface ChunkDragVisualProps {
    isVisible: boolean;
    chunk: ChunkInstance | null;
    dgml: IDocumentDgml;
}

const ChunkDragVisual = (props: ChunkDragVisualProps): JSX.Element => {
    if (!props.isVisible || !props.chunk || !props.dgml.isInitialized()) {
        return <></>
    }

    let descriptor: IconDescriptor = smallChunkDragIcon;
    const isTable = props.chunk.chunk?.structure?.type === ChunkStructure.Table;
    if (isTable) {
        descriptor = tableChunkDragIcon;
    }
    else if (DgmlInstancesSingleton.isStructuralChunk(props.chunk)) {
        descriptor = largeChunkDragIcon;
    }

    return <IconRenderer source={descriptor} width={30} height={30} />
}

const smallChunkDragIcon: IconDescriptor = {
    name: dgIconNames.DgLinkSmallChunk,
    height: 358,
    width: 512,
    defaultColor: primaryTheme.palette.black,
    shadow: cardShadow
}

const largeChunkDragIcon: IconDescriptor = {
    name: dgIconNames.DgLinkLargeChunk,
    height: 448,
    width: 512,
    defaultColor: primaryTheme.palette.black,
    shadow: cardShadow
}

const tableChunkDragIcon: IconDescriptor = {
    name: dgIconNames.DgLinkTableChunk,
    height: 448,
    width: 512,
    defaultColor: primaryTheme.palette.black,
    shadow: cardShadow
}
